import AppContext from 'App/AppContext';
import { IPCheckbox, IPSeparator, IPStack } from 'baseComponents';
import GenericModal from 'components/Dialogs/GenericModal';
import WarningMessage from 'components/Notification/WarningMessage';
import { globalStackTokensGapSmall } from 'globalStyles';
import { hasCurrentApprovedEvidence } from 'models/approval';
import { DocumentProviderCapabilities } from 'models/documentProvider';
import ResourceLink from 'models/resourceLink';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLinkApprovals } from 'services/Api/linkService';
import { apiExportWordEvidencePdf } from 'services/Api/reportService';
import { apiGetTenantLogo } from 'services/Api/tenantService';
import { apiRequest } from 'services/Auth/authConfig';
import { getSharePointErrorMsg, toAppError } from 'services/Graph/graphErrors';
import { downloadBlob, sanitizeFileName as sanitizeFileNameChars } from 'utils/download';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import ExportWordEvidencePdfRequestDTO from '../../../models/dto/Reports/ExportWordEvidencePdfRequestDTO';

export interface IWordEvidenceExportModalProps {
  isOpen: boolean;
  link?: ResourceLink;
  onClose: () => void;
}

const WORD_EVIDENCE_FILENAME_FALLBACK = 'document.pdf';

/**
 * The export renders the Word document to PDF through Microsoft Graph, so it is available
 * wherever our Word toolchain can reach the document - the same condition docxEdit states.
 * Pass the capabilities to respect it; without them the answer is the SharePoint one.
 */
export const canExportWordEvidence = (
  link: ResourceLink | undefined,
  capabilities?: DocumentProviderCapabilities,
): boolean => {
  if (capabilities && !capabilities.docxEdit) return false;

  return !!link && link.isWordDocument();
};

/** The shared character-stripper, plus this export's own rules: a `.pdf` extension, always. */
const sanitizeFileName = (fileName: string | undefined): string => {
  if (!fileName) return WORD_EVIDENCE_FILENAME_FALLBACK;

  const sanitized = sanitizeFileNameChars(fileName);
  if (!sanitized) return WORD_EVIDENCE_FILENAME_FALLBACK;
  if (sanitized.toLowerCase().endsWith('.pdf')) return sanitized;

  return `${sanitized}.pdf`;
};

const createFallbackFileName = (link: ResourceLink | undefined): string => {
  const baseName = link?.linkName?.replace(/\.docx$/i, '')?.trim();
  if (!baseName) return WORD_EVIDENCE_FILENAME_FALLBACK;
  const versionSuffix = link?.versionInfo?.version ? ` - v${link?.versionInfo?.version}` : '';

  return sanitizeFileName(`${baseName}${versionSuffix}.pdf`);
};

const WordEvidenceExportModal = (props: IWordEvidenceExportModalProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['library', 'kpi', 'translation', 'sharepoint', 'exceptions']);
  const [showCompanyLogo, setShowCompanyLogo] = useState<boolean>(true);
  const [showEvidenceHeader, setShowEvidenceHeader] = useState<boolean>(true);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState<boolean>(false);
  const [enableLinks, setEnableLinks] = useState<boolean>(true);
  const [canShowCompanyLogo, setCanShowCompanyLogo] = useState<boolean>(false);
  const [canShowEvidenceHeader, setCanShowEvidenceHeader] = useState<boolean>(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  useEffect(() => {
    if (!props.isOpen) {
      setIsLoadingOptions(false);

      return;
    }

    let isDisposed = false;
    setIsLoadingOptions(true);

    const initializeOptions = async () => {
      const storedShowCompanyLogo = getLocalStorageData(appContext, LocalStorageKeys.ExportEvidenceShowCompanyLogo);
      const storedShowEvidenceHeader = getLocalStorageData(
        appContext,
        LocalStorageKeys.ExportEvidenceShowEvidenceHeader,
      );
      const storedShowAdditionalDetails = getLocalStorageData(
        appContext,
        LocalStorageKeys.ExportEvidenceShowAdditionalDetails,
      );
      const storedEnableLinks = getLocalStorageData(appContext, LocalStorageKeys.ExportEvidenceEnableLinks);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const customLogo = await apiGetTenantLogo(
        accessToken,
        appContext.user.login.isOrgUnit ? appContext.user.login.tenantId : undefined,
      );
      const nextCanShowCompanyLogo = !!customLogo;

      let nextCanShowEvidenceHeader = false;
      if (props.link) {
        if (!props.link.approvals) {
          props.link.approvals = await apiGetLinkApprovals(props.link.linkId, accessToken, appContext.globalDataCache);
        }

        nextCanShowEvidenceHeader = hasCurrentApprovedEvidence(props.link, appContext.globalDataCache);
      }

      if (isDisposed) {
        return;
      }

      setShowCompanyLogo(nextCanShowCompanyLogo && storedShowCompanyLogo !== 'false');
      setShowEvidenceHeader(nextCanShowEvidenceHeader && storedShowEvidenceHeader !== 'false');
      setShowAdditionalDetails(
        nextCanShowEvidenceHeader && storedShowEvidenceHeader !== 'false' && storedShowAdditionalDetails === 'true',
      );
      setEnableLinks(storedEnableLinks !== 'false');
      setCanShowCompanyLogo(nextCanShowCompanyLogo);
      setCanShowEvidenceHeader(nextCanShowEvidenceHeader);
      setIsLoadingOptions(false);
    };

    initializeOptions().catch((err) => {
      if (!isDisposed) {
        setCanShowCompanyLogo(false);
        setShowCompanyLogo(false);
        setCanShowEvidenceHeader(false);
        setShowEvidenceHeader(false);
        setIsLoadingOptions(false);
        appContext.setError(err);
      }
    });

    return () => {
      isDisposed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.link]);

  const title = useMemo(() => t('library:ExportWordEvidence.Dialogs.Title'), [t]);

  const onChangeShowCompanyLogo = useCallback(
    (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
      if (!canShowCompanyLogo) {
        return;
      }

      const nextValue = checked === true;
      setShowCompanyLogo(nextValue);
      setLocalStorageData(appContext, LocalStorageKeys.ExportEvidenceShowCompanyLogo, nextValue.toString());
    },
    [appContext, canShowCompanyLogo],
  );

  const onChangeShowEvidenceHeader = useCallback(
    (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
      const nextValue = checked === true;
      setShowEvidenceHeader(nextValue);
      if (!nextValue) {
        setShowAdditionalDetails(false);
      }
      setLocalStorageData(appContext, LocalStorageKeys.ExportEvidenceShowEvidenceHeader, nextValue.toString());
    },
    [appContext],
  );

  const onChangeShowAdditionalDetails = useCallback(
    (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
      if (!canShowEvidenceHeader || !showEvidenceHeader) {
        return;
      }

      const nextValue = checked === true;
      setShowAdditionalDetails(nextValue);
      setLocalStorageData(appContext, LocalStorageKeys.ExportEvidenceShowAdditionalDetails, nextValue.toString());
    },
    [appContext, canShowEvidenceHeader, showEvidenceHeader],
  );

  const onChangeEnableLinks = useCallback(
    (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
      const nextValue = checked === true;
      setEnableLinks(nextValue);
      setLocalStorageData(appContext, LocalStorageKeys.ExportEvidenceEnableLinks, nextValue.toString());
    },
    [appContext],
  );

  const onExport = useCallback(async () => {
    if (!props.link) return;

    try {
      setIsExporting(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const request = new ExportWordEvidencePdfRequestDTO();
      request.linkId = props.link.linkId;
      request.showCompanyLogo = canShowCompanyLogo && showCompanyLogo;
      request.showEvidenceHeader = showEvidenceHeader;
      request.showAdditionalDetails = canShowEvidenceHeader && showEvidenceHeader && showAdditionalDetails;
      request.enableLinks = enableLinks;
      request.documentTitleLabel = t('library:ExportEvidence.Fields.DocumentTitle');
      request.versionLabel = t('library:ExportEvidence.Fields.Version');
      request.approvalStatusLabel = t('library:ExportEvidence.Fields.ApprovalStatus');
      request.approvedByLabel = t('library:ExportEvidence.Fields.ApprovedBy');
      request.approvedAtLabel = t('library:ExportEvidence.Fields.ApprovedAt');
      request.ownerLabel = t('library:ExportEvidence.Fields.Owner');
      request.sharePointSourceUrlLabel = t('library:ExportEvidence.Fields.SharePointUrl');
      request.exportedByLabel = t('library:ExportEvidence.Fields.ExportedBy');
      request.exportedAtLabel = t('library:ExportEvidence.Fields.ExportedAt');

      const file = await apiExportWordEvidencePdf(request, accessToken);
      downloadBlob(file, sanitizeFileName(file.name || createFallbackFileName(props.link)));
      props.onClose();
    } catch (err) {
      const expectedErrorMessage = getSharePointErrorMsg(err, t);

      if (expectedErrorMessage) {
        appContext.showNotification(expectedErrorMessage, true, false, true);
      } else {
        appContext.showNotification(t('library:ExportWordEvidence.Dialogs.Error'), true, false, true);
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canShowCompanyLogo,
    canShowEvidenceHeader,
    enableLinks,
    showAdditionalDetails,
    showCompanyLogo,
    showEvidenceHeader,
  ]);

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      title={title}
      subTitle={t('library:ExportWordEvidence.Dialogs.SubTitle', { name: props.link?.linkName ?? '' })}
      onClose={props.onClose}
      onSave={onExport}
      saveButtonText={t('library:ExportWordEvidence.Dialogs.ExportButton')}
      saveDisabled={!props.link || isExporting || isLoadingOptions}
      isActionPending={isExporting}
      minWidth={420}
      maxWidth={800}
      keepOpenAfterSave={true}
    >
      <IPSeparator />
      <IPStack tokens={globalStackTokensGapSmall}>
        <IPCheckbox
          checked={canShowCompanyLogo && showCompanyLogo}
          label={t('library:ExportEvidence.Options.ShowCompanyLogo')}
          onChange={onChangeShowCompanyLogo}
          disabled={!canShowCompanyLogo}
        />
        <IPCheckbox
          checked={canShowEvidenceHeader && showEvidenceHeader}
          label={t('library:ExportEvidence.Options.ShowEvidenceHeader')}
          onChange={onChangeShowEvidenceHeader}
          disabled={!canShowEvidenceHeader}
        />
        <div style={{ marginLeft: 26 }}>
          <IPCheckbox
            checked={canShowEvidenceHeader && showEvidenceHeader && showAdditionalDetails}
            label={t('library:ExportEvidence.Options.ShowAdditionalDetails')}
            onChange={onChangeShowAdditionalDetails}
            disabled={!canShowEvidenceHeader || !showEvidenceHeader}
          />
        </div>
        {!isLoadingOptions && !canShowEvidenceHeader && (
          <WarningMessage message={t('library:ExportEvidence.Warnings.ApprovalDataRequiresNewApproval')} />
        )}
        <IPCheckbox
          checked={enableLinks}
          label={t('library:ExportEvidence.Options.EnableLinks')}
          onChange={onChangeEnableLinks}
        />
      </IPStack>
    </GenericModal>
  );
};

export default WordEvidenceExportModal;
